import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Trash2, Plus, Minus, ArrowRight, Store } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUrl';

export default function CartPage() {
  const { cartItems, cartTotal, itemCount, removeFromCart, updateQuantity, loading, initialized } = useCart();

  const handleRemove = async (itemId, productName) => {
    try {
      await removeFromCart(itemId);
      toast.success(`${productName} removed from cart`);
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      await updateQuantity(itemId, newQuantity);
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  };

  // No delivery for now - collection only
  const finalTotal = cartTotal;

  // Show loading state while cart initializes
  if (!initialized || loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#8D6E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center" data-testid="cart-empty">
        <div className="text-center">
          <div className="w-24 h-24 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-12 h-12 text-stone-400" />
          </div>
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a] mb-2">
            Your basket is empty
          </h1>
          <p className="text-stone-500 mb-6">
            Looks like you haven't added anything yet.
          </p>
          <Link to="/shop">
            <Button className="bg-[#1a1a1a] hover:bg-[#1a1a1a]/90 rounded-full px-8">
              Start Shopping
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a]">
            Your Basket
          </h1>
          <p className="text-stone-500 mt-2">{itemCount} item{itemCount !== 1 ? 's' : ''}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item, idx) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm flex gap-4"
                data-testid={`cart-item-${item.id}`}
              >
                {/* Image */}
                <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                  <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl overflow-hidden">
                    <img
                      src={getImageUrl(item.image)}
                      alt={item.product_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </Link>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between">
                    <div>
                      <Link to={`/product/${item.product_id}`}>
                        <h3 className="font-['Playfair_Display'] font-semibold text-[#1a1a1a] hover:text-[#8D6E63] transition-colors">
                          {item.product_name}
                        </h3>
                      </Link>
                      {item.size_name && (
                        <p className="text-stone-500 text-sm mt-1">Size: {item.size_name}</p>
                      )}
                      {item.flavor_name && (
                        <p className="text-stone-500 text-sm">Flavour: {item.flavor_name}</p>
                      )}
                      {item.custom_text_full && (
                        <p className="text-[#1a1a1a] text-sm mt-1">
                          Cake text: <span className="font-handwritten text-base">"{item.custom_text_full}"</span>
                        </p>
                      )}
                      {item.cake_message && (
                        <p className="text-[#1a1a1a] text-sm mt-1">
                          Top message: <span className="font-handwritten text-base">"{item.cake_message}"</span>
                        </p>
                      )}
                      {item.special_request && (
                        <p className="text-amber-700 text-sm mt-1 italic">
                          Special request submitted (we'll review)
                        </p>
                      )}
                      {item.gift_note && (
                        <p className="text-[#8D6E63] text-sm mt-1 italic">
                          Gift note included
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemove(item.id, item.product_name)}
                      className="p-2 text-stone-400 hover:text-red-500 transition-colors"
                      disabled={loading}
                      data-testid={`remove-${item.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    {/* Quantity */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 rounded-full border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition-colors"
                        disabled={loading || item.quantity <= 1}
                        data-testid={`qty-minus-${item.id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <button
                        onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 rounded-full border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition-colors"
                        disabled={loading}
                        data-testid={`qty-plus-${item.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Price */}
                    <p className="font-bold text-[#1a1a1a]">
                      €{item.total_price.toFixed(2)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
              <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#1a1a1a] mb-6">
                Order Summary
              </h2>

              <div className="space-y-4">
                <div className="flex justify-between text-stone-600">
                  <span>Subtotal</span>
                  <span>€{cartTotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-stone-600">
                  <span>Collection</span>
                  <span className="text-green-600">FREE</span>
                </div>

                <div className="bg-[#8D6E63]/10 rounded-lg p-3 flex items-center gap-2">
                  <Store className="w-4 h-4 text-[#8D6E63]" />
                  <p className="text-sm text-[#8D6E63]">
                    Free collection from our Dublin 7 bakery
                  </p>
                </div>

                <div className="border-t border-stone-200 pt-4">
                  <div className="flex justify-between text-lg font-bold text-[#1a1a1a]">
                    <span>Total</span>
                    <span data-testid="cart-total">€{finalTotal.toFixed(2)}</span>
                  </div>
                </div>

                <Link to="/checkout" className="block">
                  <Button 
                    className="w-full bg-[#1a1a1a] hover:bg-[#1a1a1a]/90 rounded-full h-12 text-base btn-shine"
                    data-testid="checkout-btn"
                  >
                    Proceed to Checkout
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>

                <Link to="/shop" className="block">
                  <Button 
                    variant="outline" 
                    className="w-full rounded-full border-stone-300"
                  >
                    Continue Shopping
                  </Button>
                </Link>
              </div>

              {/* Promo Code Reminder */}
              <div className="mt-6 p-4 bg-stone-50 rounded-xl">
                <p className="text-sm text-stone-600">
                  First order? Use code{' '}
                  <span className="font-mono font-bold text-[#8D6E63]">MADBRO10</span>
                  {' '}at checkout for 10% off!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
